import { Expr } from "../expr/ast";
import { JsonValue, toJsonValue } from "../json";
import { StepKind } from "../registry/kinds";
import {
  TypeRef,
  TypeSystem,
  findType,
  isAssignable,
  resolvePathType,
  unwrapNamed,
} from "../types/type-system";

/**
 * Every path in a context type whose value fits `expected`, nearest first.
 * Fields that take arguments or sit inside lists are skipped: neither can be
 * bound without more input. This is what makes action availability a type
 * question: an action whose target is a record's id type is usable at a
 * step exactly when some path here has that type.
 */
export function valuesOfType(
  ts: TypeSystem,
  root: TypeRef,
  expected: TypeRef,
  maxDepth = 4,
): Expr[] {
  const found: { path: string[]; nullable: boolean }[] = [];
  const visit = (
    type: TypeRef,
    path: string[],
    nullable: boolean,
    seen: Set<string>,
  ) => {
    if (type.kind === "list") {
      return;
    }
    const named = findType(ts, unwrapNamed(type).name);
    if (
      path.length > 0 &&
      isAssignable(
        ts,
        { ...type, nullable: false },
        { ...expected, nullable: false },
      )
    ) {
      // JSON fits anything; it's never a useful suggestion.
      if (named?.name !== "JSON") {
        found.push({ path, nullable: nullable || type.nullable });
      }
      return;
    }
    if (
      named?.kind !== "object" ||
      path.length >= maxDepth ||
      seen.has(named.name)
    ) {
      return;
    }
    const next = new Set(seen).add(named.name);
    for (const [name, field] of Object.entries(named.fields)) {
      if (field.args && Object.keys(field.args).length > 0) {
        continue;
      }
      visit(field.type, [...path, name], nullable || type.nullable, next);
    }
  };
  visit(root, [], false, new Set());
  return (
    found
      // Prefer values that are always there, then trigger data over earlier steps' outputs, then shorter paths.
      .sort(
        (a, b) =>
          Number(a.nullable) - Number(b.nullable) ||
          Number(a.path[0] === "steps") - Number(b.path[0] === "steps") ||
          a.path.length - b.path.length,
      )
      .map((f) => ({ kind: "ref", path: f.path.map((field) => ({ field })) }))
  );
}

/** The entity an entity-id type identifies (OrderID → Order), if it is one. */
export function entityOf(ts: TypeSystem, type: TypeRef): string | null {
  if (type.kind !== "named") {
    return null;
  }
  const named = findType(ts, type.name);
  return named?.kind === "scalar" ? (named.entity ?? null) : null;
}

/**
 * Values that could be an action's target, best first. The run's own record
 * (`customer.id` where `customer` is a Customer) leads: when the trigger is
 * about a customer, a customer action means that customer, so it can be
 * bound without asking. Other ids of the same type stay choices.
 */
export function targetCandidates(
  ts: TypeSystem,
  root: TypeRef,
  expected: TypeRef,
): { candidates: Expr[]; obvious: Expr | null } {
  const candidates = valuesOfType(ts, root, expected);
  const entity = entityOf(ts, expected);
  const subject = candidates.filter((c) => {
    if (c.kind !== "ref" || c.path.length !== 2 || c.path[1]?.field !== "id") {
      return false;
    }
    const head = resolvePathType(ts, root, c.path.slice(0, 1));
    return head.ok && unwrapNamed(head.type).name === entity;
  });
  const [only] = candidates;
  const [first] = subject;
  if (subject.length === 1 && first) {
    return {
      candidates: [first, ...candidates.filter((c) => c !== first)],
      obvious: first,
    };
  }
  return { candidates, obvious: candidates.length === 1 && only ? only : null };
}

export type Availability =
  | { ok: true; config: JsonValue }
  | { ok: false; reason: string };

/**
 * Whether a step can be added where the context is `root`: every target it
 * needs (an entity id it acts on) must be reachable there. On success the
 * returned config has each obvious target filled in.
 */
export function availabilityAt(
  ts: TypeSystem,
  root: TypeRef,
  kind: StepKind,
): Availability {
  let config = kind.defaultConfig();
  for (const target of kind.targets) {
    const entity = entityOf(ts, target.type) ?? target.label;
    const { candidates, obvious } = targetCandidates(ts, root, target.type);
    if (candidates.length === 0) {
      return { ok: false, reason: `Needs a ${entity}` };
    }
    if (obvious) {
      config = setAt(config, target.at, toJsonValue(obvious));
    }
  }
  return { ok: true, config };
}

function setAt(doc: JsonValue, path: string[], value: JsonValue): JsonValue {
  const [head, ...rest] = path;
  if (head === undefined) {
    return value;
  }
  const obj =
    typeof doc === "object" && doc !== null && !Array.isArray(doc) ? doc : {};
  return { ...obj, [head]: setAt(obj[head] ?? null, rest, value) };
}
